import {
  ArrowTopRightOnSquareIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { CircleArrowLeft, CircleArrowRight } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

export interface TabOption {
  label: string | React.ReactNode;
  value: string;
  loading?: boolean;
  disabled?: string; // Provide reason for disabling tab as a string for the tooltip
  url?: string;
}

export interface TabsProps {
  tabs: TabOption[];
  selected: TabOption | null;
  onSelect: (tab: TabOption) => void;
  vertical?: boolean;
  className?: string;
  variant?: 'primary' | 'secondary' | 'tertiary';
  align?: 'left' | 'center';
  enableScroll?: boolean;
}

// Reusable loading spinner component
const LoadingSpinner = () => (
  <Spinner size="sm" data-testid="loading-spinner" />
);

const DisabledTabTooltip = ({ disabled }: { disabled: string }) => (
  <div className="flex items-center justify-center">
    <Tooltip title={disabled}>
      <InformationCircleIcon className="text-chariot-text-secondary size-4" />
    </Tooltip>
  </div>
);

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  selected,
  onSelect,
  vertical,
  className,
  variant = 'primary',
  align = 'center',
  enableScroll = false,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScroll = useCallback(() => {
    if (!enableScroll || !scrollContainerRef.current) {
      setShowLeftArrow(false);
      setShowRightArrow(false);
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1); // -1 for potential subpixel issues
  }, [enableScroll]);

  useEffect(() => {
    if (enableScroll && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);

      // Check scroll on tabs change or initial mount
      const observer = new MutationObserver(checkScroll);
      observer.observe(container, { childList: true, subtree: true });

      // Defer initial scroll check to avoid calling setState synchronously in effect body
      const rafId = requestAnimationFrame(checkScroll);

      return () => {
        container.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
        observer.disconnect();
        cancelAnimationFrame(rafId);
      };
    }
  }, [tabs, enableScroll, checkScroll]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const { clientWidth, scrollWidth, scrollLeft } =
        scrollContainerRef.current;
      const scrollAmount = clientWidth * 0.4;

      // Calculate the effective scroll amount, respecting boundaries
      let effectiveScrollAmount = 0;
      if (direction === 'left') {
        effectiveScrollAmount = -Math.min(scrollAmount, scrollLeft); // Don't scroll beyond 0
      } else {
        effectiveScrollAmount = Math.min(
          scrollAmount,
          scrollWidth - scrollLeft - clientWidth
        ); // Don't scroll beyond max
      }

      // Only scroll if there's an amount to scroll by
      if (Math.abs(effectiveScrollAmount) > 0) {
        scrollContainerRef.current.scrollBy({
          left: effectiveScrollAmount,
          behavior: 'smooth',
        });
      }
    }
  };

  const getTabStyles = (isSelected: boolean) => {
    if (variant === 'secondary') {
      return twMerge(
        isSelected
          ? 'bg-chariot-background-secondary text-chariot-text-primary hover:bg-chariot-background-secondary'
          : 'text-chariot-text-secondary hover:text-chariot-text-primary hover:bg-chariot-background-secondary'
      );
    }

    if (variant === 'tertiary') {
      return twMerge(
        isSelected
          ? 'rounded-none border-chariot-brand-primary text-chariot-brand-primary'
          : 'border-transparent text-chariot-text-secondary hover:border-transparent hover:text-chariot-text-primary'
      );
    }

    return twMerge(
      isSelected
        ? 'border border-chariot-text-primary bg-chariot-background-secondary text-chariot-text-primary'
        : 'border border-chariot-stroke text-chariot-text-secondary hover:border-chariot-text-primary hover:text-chariot-text-primary'
    );
  };

  const getIcon = (tab: TabOption) => {
    if (tab.disabled) {
      return <DisabledTabTooltip disabled={tab.disabled} />;
    } else if (tab.loading) {
      return <LoadingSpinner />;
    } else if (tab.url) {
      return (
        <ArrowTopRightOnSquareIcon className="text-chariot-text-secondary size-4" />
      );
    }
  };

  return (
    <TabsPrimitive.Root
      className={twMerge('relative w-full', className)}
      data-testid="tabs"
      value={selected?.value ?? undefined}
      onValueChange={value => {
        const tab = tabs.find(t => t.value === value);
        if (tab) {
          if (tab.url) {
            window.open(tab.url, '_blank', 'noopener,noreferrer');
          }
          onSelect(tab);
        }
      }}
      orientation={vertical ? 'vertical' : 'horizontal'}
      activationMode="manual"
    >
      {enableScroll && (
        <div
          className={twMerge(
            'pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-12',
            'from-chariot-background-primary bg-linear-to-r to-transparent',
            'transition-opacity duration-200 ease-in-out',
            showLeftArrow ? 'opacity-100' : 'opacity-0'
          )}
        />
      )}
      {enableScroll && showLeftArrow && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            scroll('left');
          }}
          className={twMerge(
            'absolute top-1/2 left-0 z-20 -translate-y-1/2',
            'flex h-[31px] w-[31px] items-center justify-center'
          )}
          aria-label="Scroll tabs left"
        >
          <CircleArrowLeft className="text-chariot-text-secondary size-6" />
        </Button>
      )}
      <TabsPrimitive.List
        ref={scrollContainerRef as React.RefObject<HTMLDivElement>}
        className={twMerge(
          'no-scrollbar flex flex-nowrap overflow-x-scroll will-change-scroll',
          enableScroll && 'gap-2',
          !enableScroll && 'w-full gap-4',
          vertical && 'w-full flex-col',
          variant === 'secondary' && !enableScroll && 'border-none',
          variant === 'secondary' && enableScroll && 'border-none',
          variant === 'tertiary' &&
            !vertical &&
            !enableScroll &&
            'border-chariot-stroke gap-0 border-0 border-b-2',
          variant === 'tertiary' &&
            !vertical &&
            enableScroll &&
            'border-chariot-stroke gap-0 border-0 border-b-2'
        )}
      >
        {tabs.map(tab => (
          <TabsPrimitive.Trigger
            key={tab.value}
            value={tab.value}
            data-tutorial={`tab-${tab.value}`}
            disabled={tab.disabled !== undefined}
            className={twMerge(
              'inline-flex items-center gap-2 rounded-md px-4 py-2',
              'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-hidden',
              'disabled:pointer-events-none disabled:opacity-50',
              'flex-1 whitespace-nowrap',
              vertical && 'w-full',
              'font-bold transition-colors duration-200',
              align === 'left' ? 'justify-start' : 'justify-center',
              variant === 'secondary' && 'border-none py-2 text-sm font-medium',
              variant === 'tertiary' && 'border-0 border-b-4 text-sm font-bold',
              variant === 'tertiary' && !vertical && 'size-fit flex-none',
              selected && getTabStyles(selected.value === tab.value),
              enableScroll && 'flex-none'
            )}
          >
            {tab.label}
            {getIcon(tab)}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
      {enableScroll && (
        <div
          className={twMerge(
            'pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-12',
            'from-chariot-background-primary bg-linear-to-l to-transparent',
            'transition-opacity duration-200 ease-in-out',
            showRightArrow ? 'opacity-100' : 'opacity-0'
          )}
        />
      )}
      {enableScroll && showRightArrow && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            scroll('right');
          }}
          className={twMerge(
            'absolute top-1/2 right-0 z-20 -translate-y-1/2',
            'flex h-[31px] w-[31px] items-center justify-center'
          )}
          aria-label="Scroll tabs right"
        >
          <CircleArrowRight className="text-chariot-text-secondary size-6" />
        </Button>
      )}
    </TabsPrimitive.Root>
  );
};
